import { useState } from 'react';

export function TipsFinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: '',
    units: '',
    timeline: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/public/form/submit/b4e3c2a1-8f9d-4e6b-a7c5-3d2f1e0a9b8c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyType: '',
          units: '',
          timeline: '',
          message: ''
        });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-orange-500 to-red-600 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Sell Your Apartment Building?
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-4">
            Now that you've learned these expert tips for selling an apartment building, it's time to take action. 
            We buy apartment buildings for cash and can close on your timeline. Get your free, no-obligation cash 
            offer today and discover how easy selling your property can be.
          </p>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Whether you're ready to sell now or just exploring your options, our team of experienced professionals 
            is here to help. We understand the complexities of apartment building sales and can guide you through 
            every step of the process.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Benefits */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-6">Why Sell to Us?</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">All-Cash Offers</h4>
                  <p className="text-white/80 text-sm">
                    No financing contingencies or delays. We have the funds ready to close quickly and provide 
                    certainty you can count on.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Fast Closings</h4>
                  <p className="text-white/80 text-sm">
                    Close in as little as 7-14 days or on your preferred timeline. We work around your schedule 
                    and can accommodate flexible closing dates.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-tools-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Buy As-Is</h4>
                  <p className="text-white/80 text-sm">
                    No need for repairs, renovations, or improvements. We buy properties in any condition, 
                    saving you time and money on preparations.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">No Fees or Commissions</h4>
                  <p className="text-white/80 text-sm">
                    Keep more of your profits. We don't charge commissions or hidden fees. The offer we make 
                    is what you receive at closing.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-heart-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Experienced Team</h4>
                  <p className="text-white/80 text-sm">
                    Our team has decades of combined experience buying apartment buildings. We understand the 
                    complexities and can handle any situation professionally.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-hand-heart-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Hassle-Free Process</h4>
                  <p className="text-white/80 text-sm">
                    We handle all the details and paperwork. You don't need to deal with marketing, showings, 
                    or complicated negotiations. Just a simple, straightforward sale.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Cash Offer</h3>
            <form id="tips-cta-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="123 Main Street, City, State, ZIP"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-1">
                    Property Type *
                  </label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    <option value="duplex">Duplex (2 units)</option>
                    <option value="triplex">Triplex (3 units)</option>
                    <option value="fourplex">Fourplex (4 units)</option>
                    <option value="5-10-units">5-10 Units</option>
                    <option value="11-20-units">11-20 Units</option>
                    <option value="21-50-units">21-50 Units</option>
                    <option value="50-plus-units">50+ Units</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="units" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Units *
                  </label>
                  <input
                    type="number"
                    id="units"
                    name="units"
                    value={formData.units}
                    onChange={handleChange}
                    required
                    min="2"
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="8"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
                  When Do You Want to Sell? *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select Timeline</option>
                  <option value="asap">As Soon As Possible</option>
                  <option value="1-month">Within 1 Month</option>
                  <option value="1-3-months">1-3 Months</option>
                  <option value="3-6-months">3-6 Months</option>
                  <option value="6-plus-months">6+ Months</option>
                  <option value="just-exploring">Just Exploring Options</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  placeholder="Tell us about your property, any special circumstances, or questions you have..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
              </div>

              {status === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> We've received your information and will contact you within 24 hours with your cash offer.
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error!</strong> Something went wrong. Please try again or call us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold py-4 px-8 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {status === 'submitting' ? 'Submitting...' : 'Get My Free Cash Offer Now'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy 
                and will never share your information.
              </p>
            </form>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid md:grid-cols-4 gap-8 text-center">
          <div className="text-white">
            <div className="text-4xl font-bold mb-2">500+</div>
            <div className="text-white/80 text-sm">Properties Purchased</div>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold mb-2">$200M+</div>
            <div className="text-white/80 text-sm">Total Transactions</div>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold mb-2">15+</div>
            <div className="text-white/80 text-sm">Years Experience</div>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold mb-2">4.9/5</div>
            <div className="text-white/80 text-sm">Customer Rating</div>
          </div>
        </div>
      </div>
    </section>
  );
}